import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { X, Loader2, ShieldCheck, AlertTriangle, CheckCircle2 } from 'lucide-react';

function eur(n) {
  return Number(n || 0).toLocaleString('fi-FI', { style: 'currency', currency: 'EUR' });
}

function dateFi(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('fi-FI');
}

/**
 * HealthCheckModal — ajaa /api/invoices/admin/health-check ja näyttää
 * kaikki laskut joilla matematiikka ei täsmää nykyistä 2-desim sääntöä.
 * Lukuoperaatio, ei muuta tietoja.
 */
export default function HealthCheckModal({ onClose, onOpenPreview }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);

  useEffect(() => {
    let alive = true;
    setData(null);
    setError('');
    (async () => {
      try {
        const res = await api.get(
          `/invoices/admin/health-check?include_deleted=${includeDeleted}`,
        );
        if (alive) setData(res.data);
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || e.message || 'Tarkistus epäonnistui');
      }
    })();
    return () => { alive = false; };
  }, [includeDeleted]);

  const allOk = data && data.total_with_issues === 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      data-testid="health-check-modal"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E4E7DF] shrink-0">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-[#3F6B45]" />
            <h2 className="text-lg font-medium text-[#1F2A24]">Laskujen terveystarkistus</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="health-check-close"
            className="w-8 h-8 rounded-full hover:bg-[#F4F5F0] text-[#6A6D66] hover:text-[#1F2A24] flex items-center justify-center transition-colors"
            aria-label="Sulje"
          >
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5">
          {error && (
            <div className="text-sm text-[#C05A3C] bg-[#FBEAE5] rounded px-3 py-2" data-testid="health-check-error">
              {error}
            </div>
          )}
          {!data && !error && (
            <div className="flex items-center gap-2 text-sm text-[#6A6D66]">
              <Loader2 size={16} className="animate-spin" /> Käydään läpi kaikkia laskuja…
            </div>
          )}

          {data && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Metric label="Tarkistettu" value={String(data.total_checked)} />
                <Metric
                  label="Kunnossa"
                  value={String(data.total_ok)}
                  tone="ok"
                  testId="health-check-total-ok"
                />
                <Metric
                  label="Vaatii korjausta"
                  value={String(data.total_with_issues)}
                  tone={data.total_with_issues > 0 ? 'warn' : 'ok'}
                  testId="health-check-total-issues"
                />
                <Metric label="Suonentietoon viety" value={String(data.total_exported)} />
              </div>

              <label className="flex items-center gap-2 text-sm text-[#3A4038] cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={includeDeleted}
                  onChange={(e) => setIncludeDeleted(e.target.checked)}
                  data-testid="health-check-include-deleted"
                />
                Sisällytä poistetut laskut
              </label>

              {allOk ? (
                <div
                  className="flex items-center gap-2 bg-[#E8F0E9] text-[#2A4B30] rounded px-3 py-2 text-sm"
                  data-testid="health-check-all-ok"
                >
                  <CheckCircle2 size={18} />
                  Kaikki laskut kunnossa — matematiikka täsmää 2-desim sääntöön 0,01 €:n toleranssilla.
                </div>
              ) : (
                <>
                  <div className="flex items-start gap-2 bg-[#FDF6E7] text-[#7A5A1E] rounded px-3 py-2 text-sm">
                    <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                    <div>
                      <strong>{data.total_with_issues}</strong> laskua vaatii huomiota. Voit joko
                      (a) ajaa <em>Korjaa ALV</em> -painikkeen (korjaa summat + unit_price_net),
                      tai (b) poistaa ja regeneroida ne uudestaan tuoreilla säännöillä.
                    </div>
                  </div>
                  <div className="overflow-x-auto border border-[#E4E7DF] rounded">
                    <table className="w-full text-sm">
                      <thead className="bg-[#F4F5F0] text-[#3A4038]">
                        <tr>
                          <Th>Laskun nro</Th>
                          <Th>Asiakas</Th>
                          <Th>Pvm</Th>
                          <Th>Tila</Th>
                          <Th className="text-right">Brutto (tall./oikea)</Th>
                          <Th className="text-right">Netto (tall./oikea)</Th>
                          <Th className="text-right">ALV (tall./oikea)</Th>
                          <Th>Ongelmat</Th>
                          <Th className="text-right">Toiminnot</Th>
                        </tr>
                      </thead>
                      <tbody data-testid="health-check-issues-rows">
                        {data.issues.map((iss) => (
                          <tr key={iss.id} className="border-t border-[#E4E7DF] align-top">
                            <Td className="font-medium">{iss.invoice_no}</Td>
                            <Td>{iss.customer_name}</Td>
                            <Td>{dateFi(iss.date)}</Td>
                            <Td>
                              {iss.deleted_at && <Badge tone="danger">Poistettu</Badge>}
                              {iss.exported_at && !iss.deleted_at && <Badge tone="info">Viety</Badge>}
                              {!iss.exported_at && !iss.deleted_at && <Badge tone="muted">Viemättä</Badge>}
                            </Td>
                            <Td className="text-right tabular-nums">
                              <div>{eur(iss.total_gross_stored)}</div>
                              <div className="text-xs text-[#3F6B45]">→ {eur(iss.total_gross_expected)}</div>
                            </Td>
                            <Td className="text-right tabular-nums">
                              <div>{eur(iss.total_net_stored)}</div>
                              <div className="text-xs text-[#3F6B45]">→ {eur(iss.total_net_expected)}</div>
                            </Td>
                            <Td className="text-right tabular-nums">
                              <div>{eur(iss.total_vat_stored)}</div>
                              <div className="text-xs text-[#3F6B45]">→ {eur(iss.total_vat_expected)}</div>
                            </Td>
                            <Td>
                              <ul className="list-disc list-inside space-y-0.5 text-xs text-[#6A6D66]">
                                {iss.problems.slice(0, 3).map((p, i) => (
                                  <li key={i}>{p}</li>
                                ))}
                                {iss.problems.length > 3 && (
                                  <li className="italic">+{iss.problems.length - 3} ongelmaa lisää</li>
                                )}
                              </ul>
                            </Td>
                            <Td className="text-right">
                              <button
                                type="button"
                                onClick={() => onOpenPreview && onOpenPreview(iss.id)}
                                className="text-xs text-[#3F6B45] hover:underline"
                                data-testid={`health-check-open-${iss.id}`}
                              >
                                Esikatsele
                              </button>
                            </Td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[#E4E7DF] shrink-0">
          <Button size="sm" onClick={onClose} data-testid="health-check-close-footer">Sulje</Button>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, tone, testId }) {
  const toneClass =
    tone === 'ok' ? 'bg-[#E8F0E9] text-[#2A4B30]' :
    tone === 'warn' ? 'bg-[#FDF6E7] text-[#7A5A1E]' :
    'bg-[#F4F5F0] text-[#1F2A24]';
  return (
    <div className={`rounded px-3 py-2 ${toneClass}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-xl mt-0.5 tabular-nums">{value}</div>
    </div>
  );
}

function Th({ children, className = '' }) {
  return <th className={`text-left px-2 py-1.5 text-[11px] font-medium ${className}`}>{children}</th>;
}

function Td({ children, className = '', ...rest }) {
  return <td className={`px-2 py-1.5 text-[13px] align-top ${className}`} {...rest}>{children}</td>;
}

function Badge({ children, tone = 'muted' }) {
  const cls =
    tone === 'danger' ? 'bg-[#FBEAE5] text-[#C05A3C]' :
    tone === 'info' ? 'bg-[#E8F0E9] text-[#2A4B30]' :
    'bg-[#F4F5F0] text-[#6A6D66]';
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] ${cls}`}>{children}</span>
  );
}
